import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import Layout from "../../components/Layout";
import api from "../../lib/api";
import DOMPurify from "dompurify";
import { toast } from "sonner";
import {
  Search, ChevronRight, ChevronDown, BookOpen, Edit3, Save, X,
  FileText, ArrowLeft, ArrowRight, PanelLeftClose, PanelLeftOpen, Maximize2,
} from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import "./hub-v604.css";

/* Sanitiser allowlist — the Hub v6 content has tables, custom classes, colour
   callouts, and structural divs we want to keep. */
const PURIFY_OPTS = {
  ADD_ATTR: ["target", "rel", "class", "style", "colspan", "rowspan", "data-hue",
             "data-sec", "data-tier", "data-cat", "data-key", "open"],
  ADD_TAGS: ["details", "summary", "section", "article", "figure", "figcaption"],
  FORBID_TAGS: ["script", "iframe", "object", "embed"],
  FORBID_ATTR: ["onclick", "onload", "onerror", "onmouseover", "onmouseout", "onchange", "oninput", "onsubmit"],
};

function GroupHeader({ label, open, onToggle, count }) {
  const Ico = open ? ChevronDown : ChevronRight;
  return (
    <button
      onClick={onToggle}
      className="w-full flex items-center gap-2 px-3 py-2 hover:bg-white/[0.03] transition rounded"
      data-testid={`hub-group-${label}`}
    >
      <Ico className="w-3.5 h-3.5 text-slate-500 flex-shrink-0" />
      <span className="text-[10.5px] font-bold uppercase tracking-widest font-mono text-orange-400 truncate flex-1 text-left">{label}</span>
      <span className="text-[10px] font-mono text-slate-500">{count}</span>
    </button>
  );
}

function TopicRow({ t, active, onClick, compact }) {
  return (
    <button
      onClick={onClick}
      className={`w-full flex items-center gap-2 py-1.5 text-left transition rounded ${compact ? "px-2 justify-center" : "px-3 pl-8"} ${
        active ? "bg-orange-500/15 text-orange-200" : "text-slate-300 hover:bg-white/[0.03] hover:text-white"
      }`}
      data-testid={`hub-topic-${t.key}`}
      title={t.title}
    >
      <span className="text-sm">{t.icon || "📄"}</span>
      {!compact && <span className="text-sm truncate flex-1">{t.title}</span>}
    </button>
  );
}

export default function Hub() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [searchParams, setSearchParams] = useSearchParams();
  const [groups, setGroups] = useState([]);
  const [total, setTotal] = useState(0);
  const [openMap, setOpenMap] = useState({});
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({ title: "", desc: "", body_html: "" });
  const [saving, setSaving] = useState(false);
  // Persist sidebar collapse + last topic across sessions
  const [sbCollapsed, setSbCollapsed] = useState(() => localStorage.getItem("hub.sbCollapsed") === "1");
  const [reader, setReader] = useState(false); // full-width reader mode
  useEffect(() => { localStorage.setItem("hub.sbCollapsed", sbCollapsed ? "1" : "0"); }, [sbCollapsed]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/hub/topics");
        setGroups(data.groups || []);
        setTotal(data.total || 0);
        const om = {};
        (data.groups || []).forEach((g) => { om[g.label] = true; });
        setOpenMap(om);
        const flat = (data.groups || []).flatMap((g) => g.topics);
        // Priority: ?topic= query param > localStorage > first topic
        const urlTopic = searchParams.get("topic");
        const last = localStorage.getItem("hub.lastTopic");
        const start =
          flat.find((t) => t.id === urlTopic) ||
          flat.find((t) => t.id === last) ||
          flat[0];
        if (start) setSelected(start.id);
      } catch (e) { console.error(e); }
    })();
  }, []);

  // Respond to ?topic=<id> URL changes (e.g. from the global ⌘K palette).
  useEffect(() => {
    const urlTopic = searchParams.get("topic");
    if (!urlTopic || urlTopic === selected) return;
    // Only honor once we've loaded groups (so we can validate the id).
    const flat = groups.flatMap((g) => g.topics);
    if (flat.find((t) => t.id === urlTopic)) {
      setSelected(urlTopic);
      // Clean the URL so refresh doesn't lock the topic.
      const next = new URLSearchParams(searchParams);
      next.delete("topic");
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, groups, selected, setSearchParams]);

  useEffect(() => {
    if (!selected) return;
    localStorage.setItem("hub.lastTopic", selected);
    (async () => {
      try {
        const { data } = await api.get(`/hub/topics/${selected}`);
        setDetail(data);
        setEditing(false);
        setDraft({ title: data.title || "", desc: data.desc || "", body_html: data.body_html || "" });
      } catch (e) { console.error(e); }
    })();
  }, [selected]);

  const filtered = useMemo(() => {
    if (!q.trim()) return groups;
    const n = q.trim().toLowerCase();
    return groups
      .map((g) => ({ ...g, topics: g.topics.filter((t) =>
        (t.title || "").toLowerCase().includes(n) ||
        (t.desc  || "").toLowerCase().includes(n) ||
        (t.key   || "").toLowerCase().includes(n))}))
      .filter((g) => g.topics.length > 0);
  }, [q, groups]);

  const flat = useMemo(() => groups.flatMap((g) => g.topics.map((t) => ({ ...t, group: g.label }))), [groups]);
  const currentIdx = useMemo(() => flat.findIndex((t) => t.id === selected), [flat, selected]);
  const prevT = currentIdx > 0 ? flat[currentIdx - 1] : null;
  const nextT = currentIdx >= 0 && currentIdx < flat.length - 1 ? flat[currentIdx + 1] : null;
  const currentGroup = groups.find((g) => g.topics.some((t) => t.id === selected))?.label || detail?.group;

  const save = async () => {
    if (!detail) return;
    setSaving(true);
    try {
      const clean = { ...draft, body_html: DOMPurify.sanitize(draft.body_html || "", PURIFY_OPTS) };
      const { data } = await api.patch(`/hub/topics/${detail._id}`, clean);
      setDetail(data.topic);
      setEditing(false);
      const { data: fresh } = await api.get("/hub/topics");
      setGroups(fresh.groups || []);
      toast.success("Topic saved");
    } catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  const cleanBody = useMemo(
    () => (detail?.body_html ? DOMPurify.sanitize(detail.body_html, PURIFY_OPTS) : ""),
    [detail]
  );

  return (
    <Layout>
      {/* Hub uses its own edge-to-edge layout — we break out of Layout's max-width by using -mx utility */}
      <div className="-mx-4 sm:-mx-6 lg:-mx-8 -mt-2" data-testid="hub-page">
        <div className="grid grid-cols-12 gap-0 border-t border-white/5" style={{ height: "calc(100vh - 7rem)" }}>

          {/* SIDEBAR */}
          <aside
            className={`bg-[#151F38] border-r border-white/10 flex flex-col overflow-hidden transition-all duration-200 ${
              sbCollapsed ? "col-span-1" : "col-span-12 sm:col-span-4 md:col-span-3 lg:col-span-2"
            } ${reader ? "hidden" : ""}`}
          >
            <header className="px-3 py-3 border-b border-white/10 flex items-center gap-2">
              <BookOpen className={`w-4 h-4 text-orange-400 flex-shrink-0 ${sbCollapsed ? "mx-auto" : ""}`} />
              {!sbCollapsed && (
                <div className="flex-1 min-w-0">
                  <div className="text-[10.5px] font-bold uppercase tracking-widest font-mono text-orange-400">Hub</div>
                  <div className="text-[10px] text-slate-500 font-mono truncate">{total} topics · CW Internal</div>
                </div>
              )}
              <button
                onClick={() => setSbCollapsed(!sbCollapsed)}
                className="p-1 rounded hover:bg-white/5 text-slate-400 hover:text-white"
                title={sbCollapsed ? "Expand sidebar" : "Collapse sidebar"}
                data-testid="hub-sidebar-toggle"
              >
                {sbCollapsed ? <PanelLeftOpen className="w-4 h-4" /> : <PanelLeftClose className="w-4 h-4" />}
              </button>
            </header>

            {!sbCollapsed && (
              <div className="p-2 border-b border-white/10">
                <div className="relative">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-500" />
                  <input
                    value={q} onChange={(e) => setQ(e.target.value)}
                    placeholder="Search 69 topics…"
                    className="w-full pl-8 pr-3 py-1.5 rounded-md bg-black/40 border border-white/10 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-orange-400/50"
                    data-testid="hub-search"
                  />
                </div>
              </div>
            )}

            <div className="flex-1 overflow-y-auto p-1">
              {filtered.length === 0 && !sbCollapsed && (
                <div className="text-center text-xs text-slate-500 py-6">No topics match.</div>
              )}
              {filtered.map((g) => {
                const open = openMap[g.label] !== false;
                return (
                  <div key={g.label} className="mb-1">
                    {!sbCollapsed && (
                      <GroupHeader label={g.label} open={open}
                        onToggle={() => setOpenMap((m) => ({ ...m, [g.label]: !open }))}
                        count={g.topics.length}
                      />
                    )}
                    {(sbCollapsed || open) && (
                      <div className="space-y-0.5 mt-0.5">
                        {g.topics.map((t) => (
                          <TopicRow key={t.id} t={t} active={t.id === selected} onClick={() => setSelected(t.id)} compact={sbCollapsed} />
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </aside>

          {/* CONTENT */}
          <main
            className={`bg-[#151F38] overflow-hidden flex flex-col ${
              reader ? "col-span-12" : sbCollapsed ? "col-span-11" : "col-span-12 sm:col-span-8 md:col-span-9 lg:col-span-10"
            }`}
            data-testid="hub-content"
          >
            {!detail ? (
              <div className="flex-1 grid place-items-center text-slate-500 text-sm">Select a topic to view.</div>
            ) : editing ? (
              <>
                <header className="sticky top-0 z-10 flex items-center justify-between gap-3 px-6 py-4 border-b border-white/10 bg-[#0F1A30]/90 backdrop-blur">
                  <div className="flex-1">
                    <input value={draft.title} onChange={(e) => setDraft((d) => ({ ...d, title: e.target.value }))}
                      className="w-full bg-transparent text-xl font-bold text-white focus:outline-none"
                      data-testid="hub-edit-title" />
                    <input value={draft.desc} onChange={(e) => setDraft((d) => ({ ...d, desc: e.target.value }))}
                      placeholder="Short description"
                      className="w-full mt-1 bg-transparent text-xs text-slate-400 focus:outline-none"
                      data-testid="hub-edit-desc" />
                  </div>
                  <button onClick={() => setEditing(false)}
                    className="text-xs font-mono text-slate-400 hover:text-white px-3 py-2 rounded-md border border-white/10"
                    data-testid="hub-edit-cancel">
                    <X className="w-3.5 h-3.5 inline mr-1" /> Cancel
                  </button>
                  <button onClick={save} disabled={saving}
                    className="text-xs font-mono uppercase tracking-widest px-3 py-2 rounded-md bg-orange-500 text-black font-bold hover:bg-orange-400 disabled:opacity-50"
                    data-testid="hub-edit-save">
                    <Save className="w-3.5 h-3.5 inline mr-1" /> {saving ? "Saving…" : "Save"}
                  </button>
                </header>
                <div className="flex-1 overflow-y-auto p-6">
                  <label className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">Body HTML</label>
                  <textarea value={draft.body_html}
                    onChange={(e) => setDraft((d) => ({ ...d, body_html: e.target.value }))}
                    rows={30}
                    className="w-full mt-1 px-3 py-2 rounded-md bg-black/40 border border-white/10 text-sm text-white font-mono focus:outline-none focus:border-orange-400/50 resize-y"
                    data-testid="hub-edit-body" />
                  <div className="mt-4 text-[11px] text-slate-500">Sanitised via DOMPurify. Tables, structural divs, style attrs, colour callouts preserved. Scripts stripped.</div>
                </div>
              </>
            ) : (
              <>
                <header className="sticky top-0 z-10 flex items-center justify-between gap-4 px-6 py-3 border-b border-white/10 bg-[#0F1A30]/90 backdrop-blur">
                  <div className="min-w-0 flex-1">
                    <div className="text-[10px] uppercase tracking-widest font-mono text-orange-400 flex items-center gap-2">
                      <FileText className="w-3 h-3" /> {currentGroup}
                    </div>
                    <h1 className="text-xl sm:text-2xl font-bold text-white leading-tight mt-0.5 truncate">
                      <span className="mr-2">{detail.icon}</span>{detail.title}
                    </h1>
                    {detail.desc && <p className="text-xs text-slate-400 mt-0.5 truncate">{detail.desc}</p>}
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <button onClick={() => setReader(!reader)}
                      className="text-xs font-mono uppercase tracking-widest px-2.5 py-2 rounded-md border border-white/10 text-slate-400 hover:bg-white/5 hover:text-white"
                      title={reader ? "Exit reader mode" : "Reader mode (hide sidebar)"}
                      data-testid="hub-reader-toggle">
                      <Maximize2 className="w-3.5 h-3.5" />
                    </button>
                    {isAdmin && (
                      <button onClick={() => setEditing(true)}
                        className="text-xs font-mono uppercase tracking-widest px-3 py-2 rounded-md border border-white/10 text-slate-300 hover:bg-white/5"
                        data-testid="hub-edit-btn">
                        <Edit3 className="w-3.5 h-3.5 inline mr-1" /> Edit
                      </button>
                    )}
                  </div>
                </header>

                <article className="flex-1 overflow-y-auto">
                  <div className="max-w-6xl mx-auto px-6 py-8 hub-body"
                       dangerouslySetInnerHTML={{ __html: cleanBody }} />
                </article>

                <footer className="border-t border-white/10 bg-black/30 px-6 py-3 flex items-center justify-between text-sm">
                  {prevT ? (
                    <button onClick={() => setSelected(prevT.id)} className="text-slate-400 hover:text-white inline-flex items-center gap-1.5 max-w-[45%] truncate" data-testid="hub-prev">
                      <ArrowLeft className="w-3.5 h-3.5 flex-shrink-0" /> <span className="truncate">{prevT.title}</span>
                    </button>
                  ) : <span />}
                  {nextT ? (
                    <button onClick={() => setSelected(nextT.id)} className="text-slate-400 hover:text-white inline-flex items-center gap-1.5 max-w-[45%] truncate" data-testid="hub-next">
                      <span className="truncate">{nextT.title}</span> <ArrowRight className="w-3.5 h-3.5 flex-shrink-0" />
                    </button>
                  ) : <span />}
                </footer>
              </>
            )}
          </main>
        </div>
      </div>

      {/* Bespoke Hub content styling lives in `./hub-v604.css` (scoped to .hub-body). */}
    </Layout>
  );
}
